import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NotificationManager } from 'react-notifications';

import { withStyles } from '@material-ui/core/styles';

import closeModal from 'Helpers/ModalHelper';
import { removeQuery, QueryVal } from 'Helpers/Query';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';

import useCollection from 'Hooks/useCollection';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';

import { AdvisorNoteModalItemOverride } from 'Resources/AdvisorNoteModal';

import MetaCollectionFormDrawer from 'Components/UI/Meta/MetaForm/MetaCollectionFormDrawer';
import Button from 'Components/UI/Button';

import Typography from '@material-ui/core/Typography';

const styles = theme => ({
  root: {
    width: theme.dimensions.xl,
  },
  googleLink: {
    paddingBottom: theme.spacing(3),
  },
  linkDescription: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
});

const KEY_NOTE_ID = 'noteId';

function AdvisorNoteModal({
  classes, link,
  bestNextStepChecklistItemId,
}) {
  const { householdResource } = useSimpleHousehold();
  const [noteId] = useState(QueryVal(KEY_NOTE_ID));
  const [loading, setLoading] = useState(true);

  const {
    collection: noteCollection,
    setCollection: setNoteCollection,
  } = useCollection();

  const isEdit = noteId > 0;


  function onClose() {
    removeQuery(KEY_NOTE_ID);
    closeModal();
  }

  function getTitle() {
    if (isEdit) {
      return 'Edit Note';
    }
    return link ? '' : 'Add Note';
  }

  function onSuccess() {
    NotificationManager.success('Added a Note');
    removeQuery(KEY_NOTE_ID);
    closeModal();
    window.dispatchEvent(new Event('timelineReload'));
  }

  function onDelete() {
    NotificationManager.success('Deleted Note');
    removeQuery(KEY_NOTE_ID);
    closeModal();
    window.dispatchEvent(new Event('timelineReload'));
  }

  function onFailure(err) {
    if (err.response) {
      NotificationManager.warning('You filled the form out incorrectly');
    } else {
      displayErrorMessage(err, err.errors);
    }
  }

  const getParams = () => {
    if (noteId) {
      return { id: noteId };
    }
    if (bestNextStepChecklistItemId !== null && bestNextStepChecklistItemId > 0) {
      //  This is for creating a new note related to a checklist.
      //  Limit 0 so that we don't get any filter results back.
      return { limit: 0, offset: 0, bestNextStepChecklistItemId };
    }
    return { limit: 0, offset: 0 };
  };

  useEffect(() => {
    if (
      householdResource
      && householdResource.relationship('notes')
      && householdResource.relationship('notes').link('related')
    ) {
      householdResource
        .relationship('notes')
        .link('related')
        .Get({
          metaOverrideSet: AdvisorNoteModalItemOverride,
          params: getParams(),
        })
        .then((coll) => {
          if (coll.resources().length() === 0) {
            coll.create({
              metaOverrideSet: AdvisorNoteModalItemOverride, // Override resources that are created on the collection
              addToCollection: true,
            });
          }
          setNoteCollection(coll);
        })
        .catch((err) => {
          displayErrorMessage(err, err.errors);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [householdResource]);

  useEffect(() => {
    setLoading(true);
  }, []);

  return (
    <MetaCollectionFormDrawer
      anchor="right"
      className={classes.root}
      collection={noteCollection}
      header={
        link ? (
          <div className={classes.googleLink}>
            <Typography variant="body1" className={classes.linkDescription}>
              {
                'Launch your calendar to access the details and zoom Link. (they’ll be added here soon)'
              }
            </Typography>

            <a href={link} target="_blank" rel="noopener noreferrer">
              <Button color="primary" variant="contained" padding="16px">
                {'View event on Calendar'}
              </Button>
            </a>
          </div>
        ) : null
      }
      loading={loading}
      onClose={onClose}
      onSubmit={onSuccess}
      onFailure={onFailure}
      onDelete={isEdit ? onDelete : null}
      open
      title={getTitle()}
      confirmDeleteMessage="This note will be deleted."
      subtitle={link && 'You can add meeting notes to the timeline here.'}
    />
  );
}

AdvisorNoteModal.propTypes = {
  classes: PropTypes.object.isRequired,
  link: PropTypes.string,
  bestNextStepChecklistItemId: PropTypes.string,
};
AdvisorNoteModal.defaultProps = {
  link: null,
  bestNextStepChecklistItemId: '',
};

export default withStyles(styles)(AdvisorNoteModal);
